import { Button, deepMerge } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import {
  MRT_Column,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dashboardApi } from '../../data-access/api/dashboard.api';
import useSynthesis from '../../data-access/hooks/use-synthesis/synthesis';
import { SynthesisProperties } from '../../data-access/types/synthesis';

import { SpeciesRel, useFiltersTableOptions } from '@data-access';
import {
  isMultieditRow,
  MrtEditCellCustomInput,
  MULTI_EDIT_ROW_ID,
  PfTable,
  useRowSelection,
} from '@ui';

interface PlotsDataGridProps {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: any;
}

export function PlotsDataGrid({
  rowSelection,
  setRowSelection,
}: PlotsDataGridProps) {
  const { t } = useTranslation();
  const [resetKey, setResetKey] = useState(0);
  const [currentEdits, setCurrentEdits] = useState<
    Record<
      string,
      {
        proteine_rate?: number;
        real_yield?: number;
        species?: string;
      }
    >
  >({});
  const filtersTableOptions =
    useFiltersTableOptions<SynthesisProperties>('dashboardSynthesis');

  const query = useSynthesis();

  const updateMany = useMutation({
    mutationFn: dashboardApi.updateSynthesis,
  });

  const rowSelectionProps = useRowSelection<SynthesisProperties>({
    data: query.data?.results ?? [],
    rowSelection,
    setRowSelection,
  });

  const tableColumns = useMemo<MRT_ColumnDef<SynthesisProperties>[]>(
    () => [
      {
        accessorKey: 'thumbnail',
        enableEditing: false,
        enableColumnFilter: false,
        header: '',
        enableSorting: false,
        enableColumnActions: false,
        size: 32,
        Cell: ({ cell, row }) => {
          const thumb = cell.getValue<string>();
          if (isMultieditRow(row.id)) {
            return thumb;
          }
          if (thumb) {
            return <img alt="thumbnail" src={thumb} width="32" />;
          }
        },
      },
      {
        accessorKey: 'name',
        header: t('fields.col.field-name'),
        enableEditing: false,
      },
      {
        accessorKey: 'farm.name',
        header: t('fields.col.farm-name'),
        enableEditing: false,
      },
      {
        accessorKey: 'area',
        Cell: ({ cell }) => {
          const area = cell.getValue<number>();
          return area ? `${area.toFixed(2)} ha` : '-';
        },
        header: t('common.area'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        enableEditing: false,
      },
      {
        accessorKey: 'species',
        accessorFn: (row) => row.species?.id.toString(),
        Cell: ({ row }) => {
          const species = row.original.species as SpeciesRel | undefined;
          return species ? species.name : '-';
        },
        header: t('common.crop'),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: query.data?.options.species ?? [],
        },
        enableEditing: false,
        editVariant: 'select',
        mantineEditSelectProps: {
          variant: 'default',
          allowDeselect: false,
          data: query.data?.options.species ?? [],
        },
      },
      {
        accessorKey: 'real_yield',
        header: t('common.properties.real_yield'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Edit: ({ cell, row, table }) => (
          <MrtEditCellCustomInput<SynthesisProperties>
            cell={cell}
            mantineEditNumberInputProps={{
              rightSection: `${row.original?.species?.unit ?? ''}/ha`,
              rightSectionWidth: 40,
            }}
            table={table}
            type="number"
          />
        ),
        enableEditing: true,
      },
      {
        accessorKey: 'proteine_rate',
        header: t('common.properties.proteine_rate'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Edit: ({ cell, row, table }) => (
          <MrtEditCellCustomInput<SynthesisProperties>
            cell={cell}
            mantineEditNumberInputProps={{
              rightSection: `%`,
              rightSectionWidth: 40,
            }}
            table={table}
            type="number"
          />
        ),
        enableEditing: true,
      },
    ],
    [query.data?.options.species, t],
  );

  const handleEditInputChange = (
    row: MRT_Row<SynthesisProperties>,
    column: MRT_Column<SynthesisProperties>,
    value: FormEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement> | string,
  ) => {
    setCurrentEdits((prev) => ({
      ...prev,
      [row.id]: {
        ...prev[row.id],
        [column.id]: value,
      },
    }));
  };

  //UPDATE action
  const handleSavePlot = async () => {
    const ids = Object.keys(currentEdits);

    if (ids.includes(MULTI_EDIT_ROW_ID)) {
      const edits = currentEdits[MULTI_EDIT_ROW_ID];
      updateMany.mutate(
        {
          ids: Object.keys(rowSelection).map(Number),
          ...edits,
        },
        {
          onSettled: () => {
            setCurrentEdits({});
            setRowSelection({});
          },
        },
      );
    } else {
      const promises = ids.map((id) => {
        const edits = currentEdits[id];
        return updateMany.mutateAsync({
          ids: [parseInt(id)],
          ...edits,
        });
      });
      await Promise.all(promises);
      setCurrentEdits({});
    }
  };

  const options: MRT_TableOptions<SynthesisProperties> = deepMerge<
    MRT_TableOptions<SynthesisProperties>
  >(
    {
      columns: tableColumns,
      data: query.data?.results ?? [],
      rowCount: query.data?.count || 0,

      state: {
        showProgressBars: updateMany.isPending || query.isRefetching,
        isLoading: query.isLoading,
        rowSelection,
      },
      initialState: {
        showColumnFilters: true,
      },

      enableEditing: true,
      editDisplayMode: 'table',

      onEditingRowSave: handleSavePlot,

      mantineEditTextInputProps: ({ row, column }) => ({
        onChange: (value) => {
          handleEditInputChange(row, column, value);
        },
      }),
      mantineEditSelectProps: ({ row, column }) => ({
        onChange: (value) => {
          handleEditInputChange(row, column, value!);
        },
      }),

      renderBottomToolbarCustomActions: () => (
        <Button
          disabled={Object.keys(currentEdits).length === 0}
          loading={updateMany.isPending}
          onClick={handleSavePlot}
        >
          {t('common.saveChanges')}
        </Button>
      ),

      mantinePaperProps: {
        h: '100%',
      },

      ...rowSelectionProps,
      onRowSelectionChange: (c) => {
        setRowSelection(c);
        if (Object.keys(currentEdits).length) {
          setCurrentEdits({});
          setResetKey((prev) => prev + 1);
        }
      },
    },
    filtersTableOptions,
  );

  return (
    <PfTable
      enableMultiedit
      key={resetKey}
      onMultiEditRowSave={handleSavePlot}
      options={options}
    />
  );
}

export default PlotsDataGrid;
